import React, { FunctionComponent } from 'react'
import { useTranslation } from 'react-i18next'
import { useApi } from 'components/providers/ApiProvider'
import { useMessage } from 'components/elements/Message'
import * as yup from 'yup'
import useFormal from '@kevinwolf/formal-web'
import { Checkbox, Grid, Paper } from '@material-ui/core'
import { TextField } from 'components/elements/TextField'
import { SubmitButton } from 'Elements/SubmitButton'
import { Add } from '@material-ui/icons'
import FormControlLabel from '@material-ui/core/FormControlLabel'

export interface GrantAccessFormData {
  id: number
}

export type Props = {
  data: GrantAccessFormData
}
export type Functions = {
  onSuccess(): void
}
export type AllProps = Props & Functions

const GrantAccessForm: FunctionComponent<AllProps> = ({ data, onSuccess }) => {
  const { t } = useTranslation()
  const api = useApi()
  const { showSuccess, showError } = useMessage()

  const initialValues = {
    allClients: false,
    client: '',
  }

  const schema = yup.object().shape({
    allClients: yup.boolean(),
    client: yup.string().when('allClients', {
      is: false,
      then: schema => schema.trim().matches(/^[0-9a-zA-Z.]{1,30}$/),
    }),
  })

  const formal = useFormal(initialValues, {
    schema,
    onSubmit: async values => {
      values = schema.cast(values)

      await api.grantSubscriptionOfferAccess({
        offer_id: data.id,
        client: values.allClients ? null : values.client,
      })
        .then(data => {
          if (!data || !data.status) {
            throw new Error
          }

          formal.change('client', initialValues.client)
          formal.clearErrors()

          onSuccess()

          showSuccess(t('messages.subscriptionOfferAccessGranted'))
        })
        .catch(error => {
          let message

          switch (error.code) {
            case 3:
              message = t('alerts.checkInput')
              break
            case 510:
              message = t('alerts.subscriptionOfferAccessAlreadyGranted')
              break
            case 511:
              message = t('alerts.subscriptionOfferAccessAlreadyGrantedToIndividualClients')
              break
            default:
              message = t('alerts.tryLater')
          }

          showError(`${t('alerts.couldNotGrantSubscriptionOfferAccess')} ${message}`)
        })
    },
  })

  return (
    <form {...formal.getFormProps()} noValidate>
      <Paper variant="outlined" style={{ padding: 16, marginBottom: 32 }}>
        <Grid container spacing={1}>
          <Grid item xs={12} style={{ paddingLeft: 15 }}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  {...formal.getFieldProps('allClients')}
                  checked={formal.values.allClients}
                  onChange={event => formal.change('allClients', event.target.checked)}
                />
              }
              label={t('allClients')}
            />
          </Grid>
          <TextField
            GridProps={{ style: { flex: 1 } }}
            label={t('client')}
            helperText={t('clientPhoneNumberOrUsernameHelperText')}
            size="small"
            required={!formal.values.allClients}
            {...formal.getFieldProps('client')}
            disabled={formal.values.allClients}
          />
          <SubmitButton
            GridProps={{ style: { boxSizing: 'content-box', height: 40 } }}
            buttonText={t('add')}
            startIcon={<Add />}
            size="small"
            disabled={formal.isSubmitting}
          />
        </Grid>
      </Paper>
    </form>
  )
}

export default GrantAccessForm
